import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Box, Button, Chip, Divider, Drawer, Step, StepLabel, Stepper, Table, TableBody, TableCell, TableHead, TableRow, IconButton, InputAdornment, MenuItem, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { CredentialAccess } from './CredentialAccess';
import { credentialStatusCopy } from 'src/components/property/UtilityPortalCredentials/credentialStatus';
import { DEAD_END_STATES, SOURCE_LABELS, STAGES, WORK_STATES, changeFromPrevious, laneOf, statusOf, applyBillingLink, billEntryPayload, dueLabel, formatCycle, lagExplanation, rejectBillEntry, suggestedPeriod, workStateLabel } from './queue';

/**
 * Who this bill is for, loud enough to stop the wrong one being entered.
 *
 * Previous/Next changes the row without changing the page, and company plus address are the only
 * two facts that tell one row from another.
 */
const IdentityField = _ref => {
  let label = _ref.label,
    value = _ref.value,
    mono = _ref.mono;
  return value ? /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "overline",
    color: "text.secondary",
    sx: {
      display: 'block',
      lineHeight: 1.2
    }
  }, label), /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle1",
    sx: {
      fontWeight: 600,
      fontFamily: mono ? 'monospace' : undefined
    }
  }, value)) : null;
};
const Identity = _ref2 => {
  let item = _ref2.item;
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      backgroundColor: 'grey.100',
      border: '1px solid',
      borderColor: 'grey.300',
      borderRadius: '4px',
      padding: '16px',
      marginTop: '12px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "overline",
    color: "text.secondary",
    sx: {
      lineHeight: 1.2
    }
  }, "Customer"), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    sx: {
      fontWeight: 700,
      wordBreak: 'break-word'
    }
  }, item.companyName || 'Unknown company'), /*#__PURE__*/React.createElement(Typography, {
    variant: "overline",
    color: "text.secondary",
    sx: {
      display: 'block',
      marginTop: '10px',
      lineHeight: 1.2
    }
  }, "Property"), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    sx: {
      fontWeight: 700,
      wordBreak: 'break-word'
    }
  }, item.propertyAddress || item.propertyName || 'Unknown property'), /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '12px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      columnGap: '16px',
      rowGap: '10px'
    }
  }, /*#__PURE__*/React.createElement(IdentityField, {
    label: "Utility",
    value: item.utilityTypeName
  }), /*#__PURE__*/React.createElement(IdentityField, {
    label: "Provider",
    value: item.providerName
  }), /*#__PURE__*/React.createElement(IdentityField, {
    label: "Property code",
    value: item.propertyName,
    mono: true
  }), /*#__PURE__*/React.createElement(IdentityField, {
    label: "Account",
    value: item.accountNumber,
    mono: true
  }), /*#__PURE__*/React.createElement(IdentityField, {
    label: "Cycle",
    value: formatCycle(item.year, item.month)
  })));
};
const STAGE_ORDER = ['WaitingForBill', 'ReadyToAllocate', 'ReadyToApprove'];
const day = value => value ? new Date("".concat(String(value).slice(0, 10), "T00:00:00")).toLocaleDateString(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
}) : '-';
const money = value => value == null ? '-' : "$".concat(Number(value).toFixed(2));

/**
 * The last three bills, so an amount far off the usual is caught before it is entered or approved. "None on file" only
 * from an answer that said so: while loading, or after a failed read, it would contradict the row's own last bill.
 */
const RecentBills = _ref3 => {
  let bills = _ref3.bills,
    status = _ref3.status;
  if (status !== 'loaded') {
    return /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary"
    }, status === 'failed' ? 'Could not load the past bills.' : 'Loading past bills...');
  }
  return bills !== null && bills !== void 0 && bills.length ? /*#__PURE__*/React.createElement(Table, {
    size: "small",
    "aria-label": "Last 3 bills"
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, null, "Period end"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Amount"), /*#__PURE__*/React.createElement(TableCell, null, "Billed on"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Change"))), /*#__PURE__*/React.createElement(TableBody, null, bills.map((bill, index) => {
    const change = changeFromPrevious(bills, index);
    return /*#__PURE__*/React.createElement(TableRow, {
      key: "".concat(bill.endDate, "-").concat(index)
    }, /*#__PURE__*/React.createElement(TableCell, null, day(bill.endDate)), /*#__PURE__*/React.createElement(TableCell, {
      align: "right"
    }, money(bill.amount)), /*#__PURE__*/React.createElement(TableCell, null, day(bill.billDate)), /*#__PURE__*/React.createElement(TableCell, {
      align: "right"
    }, change == null ? '-' : "".concat(change > 0 ? '+' : '').concat(change, "%")));
  }))) : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "No previous bill on file for this utility.");
};

/**
 * One queue item, open for work: claim it, get into the portal, enter the bill, or record why not.
 *
 * Dead ends are one click on purpose. An operator who cannot quickly say "no bill yet" leaves the
 * row claimed and stale.
 */
export const WorkPanel = observer(_ref4 => {
  let row = _ref4.item,
    refreshKey = _ref4.refreshKey,
    open = _ref4.open,
    onClose = _ref4.onClose,
    onClaim = _ref4.onClaim,
    onRelease = _ref4.onRelease,
    onSetState = _ref4.onSetState,
    onEnterBill = _ref4.onEnterBill,
    onPrevious = _ref4.onPrevious,
    onNext = _ref4.onNext;
  const store = useStore();
  // The row re-read on open and after every queue load: the list's copy goes stale once a save moves the row
  // off the page.
  const _useState = useState({
      rowId: null,
      data: null,
      failed: false
    }),
    _useState2 = _slicedToArray(_useState, 2),
    detail = _useState2[0],
    setDetail = _useState2[1];
  const rowId = row === null || row === void 0 ? void 0 : row.allocationTableId;
  const current = detail.rowId === rowId ? detail : null;
  const detailData = current === null || current === void 0 ? void 0 : current.data;
  const item = (detailData === null || detailData === void 0 ? void 0 : detailData.item) || row;
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    note = _useState4[0],
    setNote = _useState4[1];
  const _useState5 = useState('NoBillAvailable'),
    _useState6 = _slicedToArray(_useState5, 2),
    deadEnd = _useState6[0],
    setDeadEnd = _useState6[1];
  const _useState7 = useState({
      startDate: '',
      endDate: '',
      amount: '',
      billDate: '',
      saving: false,
      error: ''
    }),
    _useState8 = _slicedToArray(_useState7, 2),
    bill = _useState8[0],
    setBill = _useState8[1];
  const _useState9 = useState({
      id: '',
      name: '',
      uploading: false,
      error: ''
    }),
    _useState0 = _slicedToArray(_useState9, 2),
    file = _useState0[0],
    setFile = _useState0[1];
  const fileInputRef = useRef(null);
  useEffect(() => {
    setNote((item === null || item === void 0 ? void 0 : item.note) || '');
  }, [item === null || item === void 0 ? void 0 : item.allocationTableId, item === null || item === void 0 ? void 0 : item.note]);

  // Per row, not per render: a half-typed amount left behind would be submitted against the next
  // property. Keyed on the id, not the object, which is a new reference after every reload.
  const lastBillEnd = item === null || item === void 0 ? void 0 : item.lastBillEnd;
  const cadenceDays = item === null || item === void 0 ? void 0 : item.cadenceDays;
  useEffect(() => {
    setBill(_objectSpread(_objectSpread({}, suggestedPeriod({
      lastBillEnd,
      cadenceDays
    })), {}, {
      amount: '',
      billDate: '',
      saving: false,
      error: ''
    }));
    // Same reason as the amount: an attachment left behind would be filed against the next property.
    setFile({
      id: '',
      name: '',
      uploading: false,
      error: ''
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [rowId, lastBillEnd, cadenceDays]);
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    manual = _useState10[0],
    setManual = _useState10[1];
  useEffect(() => setManual(false), [rowId]);
  useEffect(() => {
    if (!rowId) return undefined;
    let cancelled = false;
    store.admin.getBillQueueItemAsync.run(rowId).then(resp => {
      if (cancelled) return;
      const data = (resp === null || resp === void 0 ? void 0 : resp.response) || resp;
      setDetail(d => data !== null && data !== void 0 && data.item ? {
        rowId,
        data,
        failed: false
      } :
      // A failed refresh keeps the history it had, but not its copy of the row: the list's is newer.
      {
        rowId,
        data: d.rowId === rowId && d.data ? _objectSpread(_objectSpread({}, d.data), {}, {
          item: null
        }) : null,
        failed: d.rowId !== rowId || !d.data
      });
    });
    return () => {
      cancelled = true;
    };
  }, [store, rowId, row === null || row === void 0 ? void 0 : row.stage, refreshKey]);
  if (!item) return null;
  const stage = item.stage || 'WaitingForBill';
  // Only while the bill is held: once it is entered the row is QA's, and bill entry would be offered twice.
  const handEntry = manual && stage === 'ReadyToAllocate';
  const stageIndex = STAGE_ORDER.indexOf(stage);
  const loginCopy = item.credentialStatus ? credentialStatusCopy(item.credentialStatus) : null;
  const link = applyBillingLink(item);
  const claimed = !!item.assignedToAccountId;
  const billRejection = rejectBillEntry(bill);

  // Upload on pick, not on save: the bill entry takes a fileId, and a failed upload has to be
  // recoverable without losing the amount and dates already typed.
  const handleFilePick = async event => {
    var _event$target$files;
    const picked = (_event$target$files = event.target.files) === null || _event$target$files === void 0 ? void 0 : _event$target$files[0];
    if (!picked) return;
    setFile({
      id: '',
      name: picked.name,
      uploading: true,
      error: ''
    });
    try {
      // The file API answers { isSuccessful, response: <fileId> } - the id IS the response, not a
      // field on it. Reading `.fileId` meant every upload threw. Same shape and the same presigned
      // branch the shared DropFileUploader uses.
      const resp = store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled ? await store.files.uploadFileViaPresignedUrlExperimentAsync.run({
        file: picked,
        createdBy: store.account.userProfile.id
      }) : await store.files.uploadFileAsync.run({
        file: picked
      });
      if (!(resp !== null && resp !== void 0 && resp.isSuccessful) || !resp.response) throw new Error('upload failed');
      setFile({
        id: resp.response,
        name: picked.name,
        uploading: false,
        error: ''
      });
    } catch (ex) {
      setFile({
        id: '',
        name: '',
        uploading: false,
        error: 'Could not upload that file. The bill can still be saved without it.'
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const handleFileClear = () => {
    setFile({
      id: '',
      name: '',
      uploading: false,
      error: ''
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleSaveBill = async () => {
    if (billRejection) {
      setBill(b => _objectSpread(_objectSpread({}, b), {}, {
        error: billRejection
      }));
      return;
    }
    setBill(b => _objectSpread(_objectSpread({}, b), {}, {
      saving: true,
      error: ''
    }));
    try {
      await onEnterBill(item, billEntryPayload(bill, file.id));
    } catch (ex) {
      // Does not claim the bill was not saved: the create commits before the allocation
      // recalculates. Reopening is safe, a row that has a bill is refused rather than duplicated.
      setBill(b => _objectSpread(_objectSpread({}, b), {}, {
        saving: false,
        // The caller throws an Error carrying the server's own wording, so read .message. Reading
        // .response.data.message only ever matched a raw axios error, which never reaches here.
        error: (ex === null || ex === void 0 ? void 0 : ex.message) || 'Could not confirm the bill. Close this and reopen the row before trying again.'
      }));
    }
  };
  return /*#__PURE__*/React.createElement(Drawer, {
    anchor: "right",
    open: open,
    onClose: onClose,
    PaperProps: {
      sx: {
        width: {
          xs: '100%',
          md: 560
        }
      }
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      padding: '24px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    alignItems: "center",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, "Work this bill"), /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": "Close",
    onClick: onClose
  }, "\xD7")), /*#__PURE__*/React.createElement(Identity, {
    item: item
  }), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    sx: {
      marginTop: '12px'
    },
    flexWrap: "wrap",
    useFlexGap: true
  }, /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    variant: "outlined",
    color: laneOf(item.lane).color,
    label: laneOf(item.lane).label
  }), /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    color: statusOf(item.status).color,
    label: statusOf(item.status).label
  }), /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    label: workStateLabel(item.workState)
  }), stage === 'WaitingForBill' ? /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    label: dueLabel(item),
    color: item.isOverdue ? 'warning' : 'default'
  }) : null), /*#__PURE__*/React.createElement(Stepper, {
    activeStep: stageIndex,
    alternativeLabel: true,
    sx: {
      marginTop: '16px'
    }
  }, STAGE_ORDER.map(key => /*#__PURE__*/React.createElement(Step, {
    key: key,
    completed: STAGE_ORDER.indexOf(key) < stageIndex
  }, /*#__PURE__*/React.createElement(StepLabel, null, STAGES[key])))), loginCopy && loginCopy.problem ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      marginTop: '12px'
    }
  }, loginCopy.text) : null, /*#__PURE__*/React.createElement(Divider, {
    sx: {
      marginY: '16px'
    }
  }), stage === 'ReadyToAllocate' ? /*#__PURE__*/React.createElement(Stack, {
    spacing: "12px",
    sx: {
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, "Next: allocate the received bill"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "Received by ".concat(SOURCE_LABELS[item.receivedSource] || 'upload').concat(item.receivedOn ? " on ".concat(day(item.receivedOn)) : '', ". Allocate it on the Bills page; the row moves to Ready to approve once the bill is saved.")), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px"
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "contained",
    disabled: !item.receivedBillId || !item.companyId,
    onClick: () => window.open("/bills?billId=".concat(item.receivedBillId, "&companyId=").concat(item.companyId), '_blank', 'noopener,noreferrer')
  }, "Open received bill to allocate"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButton",
    onClick: () => setManual(m => !m)
  }, "Enter it by hand instead")), handEntry ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning"
  }, "Bill automation already holds this bill. Entering it here links that document, so do not also allocate it from the Bills page.") : null) : null, stage === 'ReadyToApprove' ? /*#__PURE__*/React.createElement(Stack, {
    spacing: "12px",
    sx: {
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, "Next: QA review"), detailData !== null && detailData !== void 0 && detailData.enteredBill ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "Entered bill: ".concat(money(detailData.enteredBill.amount), " for ").concat(day(detailData.enteredBill.startDate), " to ").concat(day(detailData.enteredBill.endDate))) : null, item.workState === 'ErrorWithAllocation' ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning"
  }, "QA sent this back".concat(item.note ? ": ".concat(item.note) : '.')) : null, /*#__PURE__*/React.createElement(Alert, {
    severity: "info"
  }, "QA approves in Apply Billing. Approving is what charges residents, so it never happens from the queue."), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px"
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "contained",
    disabled: !link,
    onClick: () => window.open(link, '_blank', 'noopener,noreferrer')
  }, "Open in Apply Billing to approve"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButton",
    onClick: () => onSetState(item, 'ErrorWithAllocation', note)
  }, "Send back to allocation")), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    label: "Note for the BA",
    multiline: true,
    minRows: 2,
    value: note,
    onChange: e => setNote(e.target.value)
  })) : null, stage === 'WaitingForBill' || handEntry ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, "1. Claim it"), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    sx: {
      marginTop: '8px'
    }
  }, claimed ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      alignSelf: 'center'
    }
  }, "Held by ".concat(item.assignedToName || 'someone')), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButton",
    onClick: () => onRelease(item)
  }, "Release")) : /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "contained",
    onClick: () => onClaim(item)
  }, "Claim")), /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    sx: {
      marginTop: '16px'
    }
  }, "2. Get into the portal"), /*#__PURE__*/React.createElement(CredentialAccess, {
    item: item,
    onOpenPortal: url => window.open(url || 'about:blank', '_blank', 'noopener,noreferrer')
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    sx: {
      marginTop: '16px'
    }
  }, "3. Enter the bill"), /*#__PURE__*/React.createElement(Stack, {
    spacing: "20px",
    sx: {
      marginTop: '12px'
    }
  }, item.lastBillEnd ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "Last bill ended ".concat(item.lastBillEnd).concat(item.lastBillAmount != null ? " at $".concat(Number(item.lastBillAmount).toFixed(2)) : '', ", about every ").concat(item.cadenceDays, " days.")), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, lagExplanation(item))) : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "No previous bill on file for this utility."), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px"
  }, /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    type: "date",
    label: "Service start",
    fullWidth: true,
    InputLabelProps: {
      shrink: true
    },
    value: bill.startDate,
    onChange: e => setBill(b => _objectSpread(_objectSpread({}, b), {}, {
      startDate: e.target.value,
      error: ''
    }))
  }), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    type: "date",
    label: "Service end",
    fullWidth: true,
    InputLabelProps: {
      shrink: true
    },
    value: bill.endDate,
    onChange: e => setBill(b => _objectSpread(_objectSpread({}, b), {}, {
      endDate: e.target.value,
      error: ''
    }))
  })), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px"
  }, /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    type: "number",
    label: "Bill amount",
    placeholder: "0.00",
    fullWidth: true,
    InputProps: {
      startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "start"
      }, "$")
    },
    inputProps: {
      min: 0,
      step: '0.01'
    },
    value: bill.amount,
    onChange: e => setBill(b => _objectSpread(_objectSpread({}, b), {}, {
      amount: e.target.value,
      error: ''
    }))
  }), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    type: "date",
    label: "Bill date (optional)",
    fullWidth: true,
    InputLabelProps: {
      shrink: true
    },
    helperText: "As printed on the bill",
    value: bill.billDate,
    onChange: e => setBill(b => _objectSpread(_objectSpread({}, b), {}, {
      billDate: e.target.value,
      error: ''
    }))
  })), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px",
    alignItems: "center",
    flexWrap: "wrap"
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButtonInvert",
    disabled: !claimed || file.uploading,
    onClick: () => {
      var _fileInputRef$current;
      return (_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 ? void 0 : _fileInputRef$current.click();
    }
  }, file.uploading ? 'Uploading...' : 'Attach bill file'), file.id ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, file.name), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButton",
    onClick: handleFileClear
  }, "Remove")) : /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "Optional"), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    accept: ".pdf,.png,.jpg,.jpeg,.tif,.tiff",
    style: {
      display: 'none'
    },
    onChange: handleFilePick
  })), file.error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning"
  }, file.error) : null, bill.error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error"
  }, bill.error) : null, !claimed ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning"
  }, "Claim it first so nobody enters this bill twice.") : null, /*#__PURE__*/React.createElement(Alert, {
    severity: "info"
  }, "Calculated, not billed. QA reviews and approves this allocation before any resident is charged."), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    disabled: !claimed || bill.saving || !!billRejection,
    onClick: handleSaveBill
  }, bill.saving ? 'Saving...' : 'Save bill and send to QA'), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButton",
    disabled: !link,
    onClick: () => window.open(link, '_blank', 'noopener,noreferrer')
  }, "Open in billing instead")), /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    sx: {
      marginTop: '16px'
    }
  }, "4. Or record a dead end"), /*#__PURE__*/React.createElement(Stack, {
    spacing: "20px",
    sx: {
      marginTop: '12px'
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    select: true,
    size: "small",
    label: "Reason",
    value: deadEnd,
    onChange: e => setDeadEnd(e.target.value)
  }, DEAD_END_STATES.map(value => /*#__PURE__*/React.createElement(MenuItem, {
    key: value,
    value: value
  }, WORK_STATES[value]))), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    label: "Note",
    multiline: true,
    minRows: 2,
    value: note,
    onChange: e => setNote(e.target.value)
  }), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px"
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButton",
    onClick: () => onSetState(item, deadEnd, note)
  }, "Save reason"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "contained",
    onClick: () => onSetState(item, 'ReadyForQa', note)
  }, "Mark ready for QA")))) : null, /*#__PURE__*/React.createElement(Divider, {
    sx: {
      marginY: '16px'
    }
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, "Last 3 bills"), /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '8px'
    }
  }, /*#__PURE__*/React.createElement(RecentBills, {
    bills: detailData === null || detailData === void 0 ? void 0 : detailData.recentBills,
    status: detailData ? 'loaded' : current !== null && current !== void 0 && current.failed ? 'failed' : 'loading'
  })), /*#__PURE__*/React.createElement(Divider, {
    sx: {
      marginY: '16px'
    }
  }), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButton",
    onClick: onPrevious
  }, "Previous"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButton",
    onClick: onNext
  }, "Next"))));
});